import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Switch, TextField, Typography, Stack,
} from '@mui/material';

const DEFAULT_RULES = [
  { type: 'LATE_CHECKOUT_NEXT_DAY', label: 'เช็คเอาท์วันถัดไป / ไม่ได้เช็คเอาท์', threshold_minutes: 0, deduct_minutes: 60, enabled: true },
  { type: 'LATE_ARRIVAL', label: 'มาสาย', threshold_minutes: 15, deduct_minutes: 30, enabled: true },
];

const LABEL_MAP = {
  LATE_CHECKOUT_NEXT_DAY: 'เช็คเอาท์วันถัดไป / ไม่ได้เช็คเอาท์',
  LATE_ARRIVAL: 'มาสาย',
};

export default function AttendanceRulesPanel({ info, onSave, showFormulas = false, onShowFormulasChange, salaryMode = 'late-deduct', onSalaryModeChange }) {
  const [rules, setRules] = useState(DEFAULT_RULES);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    const savedRules = info?.setting?.payroll?.attendance_rules;
    if (savedRules && savedRules.length > 0) {
      setRules(savedRules);
      initialized.current = true;
    }
  }, [info]);

  const handleChange = useCallback(async (index, field, value) => {
    const updated = rules.map((r, i) => i === index ? { ...r, [field]: value } : r);
    setRules(updated);
    if (onSave) await onSave({ 'setting.payroll.attendance_rules': updated });
  }, [rules, onSave]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ px: 2, py: 0.75, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" noWrap>
          เงื่อนไข:
        </Typography>

        {rules.map((rule, i) => (
          <Stack key={rule.type} direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Switch
              checked={rule.enabled}
              onChange={(e) => handleChange(i, 'enabled', e.target.checked)}
              size="small"
            />
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{LABEL_MAP[rule.type] || rule.label}</Typography>
            {rule.type === 'LATE_ARRIVAL' && (
              <>
                <Typography variant="caption" color="text.disabled" noWrap>เกณฑ์</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={rule.threshold_minutes}
                  onChange={(e) => handleChange(i, 'threshold_minutes', Number(e.target.value))}
                  inputProps={{ min: 0, style: { textAlign: 'center', padding: '2px 4px', fontSize: '0.75rem' } }}
                  sx={{ '& .MuiOutlinedInput-root': { height: 26, width: 48 }, '& input': { textAlign: 'center' } }}
                />
                <Typography variant="caption" color="text.disabled" noWrap>นาที</Typography>
              </>
            )}
            <Typography variant="caption" color="text.disabled" noWrap>หัก</Typography>
            <TextField
              size="small"
              type="number"
              value={rule.deduct_minutes}
              onChange={(e) => handleChange(i, 'deduct_minutes', Number(e.target.value))}
              inputProps={{ min: 0, style: { textAlign: 'center', padding: '2px 4px', fontSize: '0.75rem' } }}
              sx={{ '& .MuiOutlinedInput-root': { height: 26, width: 48 }, '& input': { textAlign: 'center' } }}
            />
            <Typography variant="caption" color="text.disabled" noWrap>นาที</Typography>
            {i < rules.length - 1 && (
              <Box sx={{ width: '1px', height: 20, bgcolor: 'divider', mx: 0.5 }} />
            )}
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, borderRadius: 999, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight={600} noWrap color="text.secondary">
            ค่าแรงตามชั่วโมงทำงาน
          </Typography>
          <Switch
            size="small"
            checked={false}
            disabled={true}
            color="warning"
            inputProps={{ 'aria-label': 'mode toggle locked' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.75, borderRadius: 999, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ color: showFormulas ? 'text.primary' : 'text.disabled' }}>
            ⓘ สูตร
          </Typography>
          <Switch
            size="small"
            checked={showFormulas}
            onChange={(e) => onShowFormulasChange?.(e.target.checked)}
            color="info"
          />
        </Box>
      </Stack>
    </Stack>
  );
}

